import type { StoreApi } from 'zustand'
import type { AppState } from '@/store/rootTypes'
import { activeWorkspacePatch } from '@/store/workspace'
import {
  awardXP as gameAwardXP, calculateXP, updateCombo, checkAchievements, unlockNextUnit,
  shouldRefillEnergy, refillEnergy, generateDailyQuest, isQuestExpired, updateQuestProgress,
  checkMasteryAchieved, awardSkillStar, recordCheckpoint, type SessionStats, type PowerUpType,
  type XPEvent,
} from '@/engine/gameEngine'
import { updatePersonalizationOnAnswer, refreshInsights, shouldChangeLayer, type SkillCategory } from '@/engine/personalization'
import { isQualifiedEvidence } from '@/entities/attempt'
import { projectLearningAttemptPatch, resolveLearningAttemptReplay } from '@/store/slices/learningSlice'
import type { LearningLayer } from '@/types'

type SetState = StoreApi<AppState>['setState']
type GetState = StoreApi<AppState>['getState']

type CorrectAnswerPatch = Pick<AppState, 'game' | 'sessionStats' | 'lastXpEvent' | 'pendingAchievements'>
type WrongAnswerPatch = Pick<AppState, 'game' | 'sessionStats'>
type LayerChangeResult = { triggered: boolean; newLayer?: LearningLayer; reason?: string }

function projectCorrectAnswer(
  state: AppState,
  action: string,
  word?: string,
  category?: SkillCategory,
  responseTimeMs?: number,
): { patch: CorrectAnswerPatch | null; xpEvent: XPEvent } {
  const stats = state.sessionStats
  if (!stats) return { patch: null, xpEvent: calculateXP(action as Parameters<typeof calculateXP>[0], 0, false) }

  const newCombo = updateCombo(state.game.combo, true)
  const xpEvent = calculateXP(action as Parameters<typeof calculateXP>[0], newCombo, false)
  const { state: newGameState } = gameAwardXP(state.game, xpEvent)
  const learnedNewWord = Boolean(word && category && !state.game.wordGems.includes(word))
  let newStats: SessionStats = {
    ...stats,
    correctCount: stats.correctCount + 1,
    totalCount: stats.totalCount + 1,
    maxCombo: Math.max(stats.maxCombo, newCombo),
    wordsLearned: stats.wordsLearned + (learnedNewWord ? 1 : 0),
    fastestCorrect: responseTimeMs
      ? (stats.fastestCorrect === 0 ? responseTimeMs : Math.min(stats.fastestCorrect, responseTimeMs))
      : stats.fastestCorrect,
  }
  let projectedGame = {
    ...newGameState,
    combo: newCombo,
    maxCombo: Math.max(newGameState.maxCombo, newCombo),
    wordGems: learnedNewWord && word ? [...newGameState.wordGems, word] : newGameState.wordGems,
  }

  if (word && category) {
    const oldMastery = state.game.personalization.srsCards[word]?.mastery ?? 0
    const personalization = updatePersonalizationOnAnswer(
      state.game.personalization,
      word,
      category,
      true,
      responseTimeMs,
    )
    const newMastery = personalization.srsCards[word]?.mastery ?? 0
    const masteryResult = checkMasteryAchieved(projectedGame, word, newMastery, oldMastery)
    projectedGame = { ...projectedGame, personalization }
    if (masteryResult.mastered && masteryResult.xpEvent) {
      const masteryAward = gameAwardXP(projectedGame, masteryResult.xpEvent)
      projectedGame = { ...masteryAward.state, personalization }
      newStats = { ...newStats, wordsMastered: [...newStats.wordsMastered, word] }
    }
    if (masteryResult.state.masteryStreak !== projectedGame.masteryStreak) {
      projectedGame = {
        ...projectedGame,
        masteryStreak: masteryResult.state.masteryStreak,
        totalMastered: masteryResult.state.totalMastered,
      }
    }
    if (!newStats.categoriesPracticed.includes(category)) {
      newStats = { ...newStats, categoriesPracticed: [...newStats.categoriesPracticed, category] }
    }
    const skillStat = personalization.weaknessProfile.skills[category]
    if (skillStat.attempts >= 5 && skillStat.recentAccuracy >= 0.7) {
      projectedGame = awardSkillStar(projectedGame, category)
    }
  }

  const newAchievements = checkAchievements(projectedGame, newStats)
  const pending = newAchievements.map((achievement) => achievement.id)
  const awardedGameState = newAchievements.reduce((game, achievement) => ({
    ...game,
    xp: game.xp + achievement.reward,
    achievements: [...game.achievements, achievement.id],
  }), projectedGame)
  let quest = state.game.dailyQuest
  if (quest && !isQuestExpired(quest)) {
    quest = updateQuestProgress(quest, action.includes('word') ? 'words' : 'perfect', 1)
  } else if (quest && isQuestExpired(quest)) {
    quest = generateDailyQuest()
  }
  return {
    patch: {
      game: { ...awardedGameState, dailyQuest: quest },
      sessionStats: newStats,
      lastXpEvent: xpEvent,
      pendingAchievements: [...state.pendingAchievements, ...pending],
    },
    xpEvent,
  }
}

function projectWrongAnswer(state: AppState, word?: string, category?: SkillCategory): WrongAnswerPatch | null {
  const stats = state.sessionStats
  if (!stats) return null
  let newStats: SessionStats = { ...stats, totalCount: stats.totalCount + 1 }
  let personalization = state.game.personalization
  if (word && category) {
    personalization = updatePersonalizationOnAnswer(state.game.personalization, word, category, false)
    if (!newStats.categoriesPracticed.includes(category)) {
      newStats = { ...newStats, categoriesPracticed: [...newStats.categoriesPracticed, category] }
    }
  }
  let masteryStreak = state.game.masteryStreak
  if (word) {
    const card = personalization.srsCards[word]
    if (card && card.mastery < 85) masteryStreak = 0
  }
  return {
    game: { ...state.game, combo: 0, personalization, masteryStreak },
    sessionStats: newStats,
  }
}

function projectLayerChange(state: AppState, currentLayer: LearningLayer): {
  patch: Pick<AppState, 'game' | 'lastLayerChangeReason'> | null
  result: LayerChangeResult
} {
  const change = shouldChangeLayer(state.game.personalization.difficulty, currentLayer)
  if (!change) return { patch: null, result: { triggered: false } }
  const game = {
    ...state.game,
    personalization: {
      ...state.game.personalization,
      difficulty: {
        ...state.game.personalization.difficulty,
        recentResults: [],
        correctStreak: 0,
        wrongStreak: 0,
      },
    },
  }
  return {
    patch: { lastLayerChangeReason: change.reason, game },
    result: { triggered: true, newLayer: change.newLayer, reason: change.reason },
  }
}

export function createGameSlice(set: SetState, get: GetState): Pick<AppState,
  'startSession' | 'recordCorrect' | 'recordWrong' | 'recordClassroomAnswer' | 'usePowerUp' | 'completeLesson' |
  'getDailyQuest' | 'initializeDailyQuest' | 'checkAndRefillEnergy' | 'clearPendingAchievements' |
  'getRecommendations' | 'getInsights' | 'recordCheckpoint' | 'checkLayerChange'> {
  return {
startSession: () => {
  set({ sessionStats: {
    correctCount: 0, totalCount: 0, maxCombo: 0, wordsLearned: 0,
    perfectScenes: 0, layerChanges: 0, startTime: Date.now(),
    categoriesPracticed: [], wordsMastered: [], fastestCorrect: 0, reflectionsCompleted: 0,
  }})
},

recordCorrect: (action: string, word?: string, category?: SkillCategory, responseTimeMs?: number) => {
  const state = get()
  const projection = projectCorrectAnswer(state, action, word, category, responseTimeMs)
  if (projection.patch) {
    set({ ...projection.patch, ...activeWorkspacePatch(state, { game: projection.patch.game }) })
  }
  return projection.xpEvent
},

recordWrong: (word?: string, category?: SkillCategory) => {
  const state = get()
  const patch = projectWrongAnswer(state, word, category)
  if (patch) set({ ...patch, ...activeWorkspacePatch(state, { game: patch.game }) })
},

recordClassroomAnswer: (input) => {
  let receipt: ReturnType<AppState['recordClassroomAnswer']>
  set((state) => {
    const attempt = input.attempt
    if (attempt.source !== 'classroom' || attempt.evidenceKind !== 'formative') {
      throw new Error('Classroom answers must be formative classroom evidence.')
    }
    const replay = resolveLearningAttemptReplay(state, attempt, 'Classroom')
    if (replay.status === 'duplicate') {
      receipt = { status: 'duplicate', layerChange: { triggered: false } }
      return state
    }

    const attemptProjection = projectLearningAttemptPatch(state, replay.attempt)
    let projectedState: AppState = {
      ...state,
      ...attemptProjection.workspacePatch,
      pendingAchievements: attemptProjection.pendingAchievements,
    }
    let answerPatch: CorrectAnswerPatch | WrongAnswerPatch | null = null
    let xpEvent: XPEvent | undefined
    if (isQualifiedEvidence(attempt)) {
      if (attempt.correct) {
        const correctProjection = projectCorrectAnswer(
          projectedState,
          input.correctAction,
          input.word,
          input.category,
          input.responseTimeMs,
        )
        answerPatch = correctProjection.patch
        xpEvent = correctProjection.xpEvent
      } else {
        answerPatch = projectWrongAnswer(projectedState, input.word, input.category)
      }
    }
    if (answerPatch) projectedState = { ...projectedState, ...answerPatch }
    const layerProjection = projectLayerChange(projectedState, state.getEffectiveLayer())
    if (layerProjection.patch) projectedState = { ...projectedState, ...layerProjection.patch }
    const workspacePatch = {
      ...attemptProjection.workspacePatch,
      game: projectedState.game,
      ...(layerProjection.patch ? { lastLayerChangeReason: projectedState.lastLayerChangeReason } : {}),
    }
    receipt = {
      status: 'recorded',
      ...(xpEvent ? { xpEvent } : {}),
      layerChange: layerProjection.result,
    }
    return {
      ...attemptProjection.workspacePatch,
      pendingAchievements: attemptProjection.pendingAchievements,
      ...(answerPatch ?? {}),
      ...(layerProjection.patch ?? {}),
      ...activeWorkspacePatch(state, workspacePatch),
    }
  })
  return receipt!
},

usePowerUp: (type: PowerUpType) => {
  const state = get()
  if (state.game.powerUps[type] <= 0) return
  const game = { ...state.game, powerUps: { ...state.game.powerUps, [type]: state.game.powerUps[type] - 1 } }
  set({ game, ...activeWorkspacePatch(state, { game }) })
},

completeLesson: (lessonId: string, unitId: string) => {
  const state = get()
  if (state.game.completedLessons.includes(lessonId)) return
  const stats = state.sessionStats
  const xpEvent = calculateXP('lessonComplete', state.game.combo, false)
  const { state: newGameState } = gameAwardXP(state.game, xpEvent)
  const unlockedState = unlockNextUnit(unitId, newGameState)
  const newCompletedLessons = [...new Set([...state.game.completedLessons, lessonId])]

  // Refresh insights after lesson completion
  let personalization = newGameState.personalization
  if (stats) {
    personalization = refreshInsights(personalization, {
      correctCount: stats.correctCount,
      totalCount: stats.totalCount,
      maxCombo: stats.maxCombo,
    })
  }

  // Update quest
  let quest = state.game.dailyQuest
  if (quest && !isQuestExpired(quest)) {
    quest = updateQuestProgress(quest, 'lessons', 1)
  } else if (quest && isQuestExpired(quest)) {
    quest = generateDailyQuest()
  }

  const game = { ...unlockedState, completedLessons: newCompletedLessons, dailyQuest: quest, combo: 0, personalization }
  const completedTasks = { ...state.completedTasks, [lessonId]: true }
  const completedAt = state.taskCompletedAt[lessonId] ?? new Date().toISOString()
  const taskCompletedAt = { ...state.taskCompletedAt, [lessonId]: completedAt }
  const weeklyPlanActive = true
  const weeklyPlanStartDate = state.weeklyPlanStartDate ?? completedAt
  set({
    game,
    sessionStats: null,
    completedTasks,
    taskCompletedAt,
    weeklyPlanActive,
    weeklyPlanStartDate,
    ...activeWorkspacePatch(state, { game, completedTasks, taskCompletedAt, weeklyPlanActive, weeklyPlanStartDate }),
  })
},

getDailyQuest: () => {
  const state = get()
  const quest = state.game.dailyQuest
  return !quest || isQuestExpired(quest) ? generateDailyQuest() : quest
},

initializeDailyQuest: () => {
  const state = get()
  if (state.game.dailyQuest && !isQuestExpired(state.game.dailyQuest)) return
  const game = { ...state.game, dailyQuest: generateDailyQuest() }
  set({ game, ...activeWorkspacePatch(state, { game }) })
},

checkAndRefillEnergy: () => {
  const state = get()
  if (shouldRefillEnergy(state.game)) {
    const game = refillEnergy(state.game)
    set({ game, ...activeWorkspacePatch(state, { game }) })
  }
},

clearPendingAchievements: () => set({ pendingAchievements: [] }),

// === Personalization Actions ===
getRecommendations: () => {
  const state = get()
  return state.game.personalization.recommendations
},

getInsights: () => {
  const state = get()
  const stats = state.sessionStats || {
    correctCount: 0, totalCount: 0, maxCombo: 0,
  }
  return refreshInsights(state.game.personalization, stats).insights
},

recordCheckpoint: () => {
  const state = get()
  const newGameState = recordCheckpoint(state.game)
  const stats = state.sessionStats
  if (stats) {
    set({
      game: newGameState,
      sessionStats: { ...stats, reflectionsCompleted: stats.reflectionsCompleted + 1 },
      ...activeWorkspacePatch(state, { game: newGameState }),
    })
  } else {
    set({ game: newGameState, ...activeWorkspacePatch(state, { game: newGameState }) })
  }
},

checkLayerChange: () => {
  const state = get()
  const projection = projectLayerChange(state, state.getEffectiveLayer())
  if (projection.patch) set({ ...projection.patch, ...activeWorkspacePatch(state, projection.patch) })
  return projection.result
},
  }
}
